import { useState, useEffect, useCallback } from 'react';
import { contentService } from '../services/content';
import { Beat, Verse, ContentFilters } from '../types/content';

export const HOME_GENRES = [
  'Hip Hop',
  'Trap',
  'Boom Bap',
  'Lo-Fi',
  'Drill',
  'Grime',
  'Experimental',
  'Old School',
  'R&B',
  'Pop Rap',
];

export const HOME_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Puducherry',
];

export const HOME_PAGE_SIZE = 3;

export function useHomeFeed() {
  const [activeTab, setActiveTab] = useState<'beats' | 'verses'>('beats');
  const [beats, setBeats] = useState<Beat[]>([]);
  const [verses, setVerses] = useState<Verse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ContentFilters>({
    sort: 'recent',
    genre: '',
    state: '',
    beatType: '',
  });
  const [page, setPage] = useState(1);
  const [totalBeats, setTotalBeats] = useState(0);
  const [totalVerses, setTotalVerses] = useState(0);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { state: _rapperState, ...verseFilters } = filters;

      if (activeTab === 'beats') {
        const beatsData = await contentService.getBeats({
          ...filters,
          offset: (page - 1) * HOME_PAGE_SIZE,
          limit: HOME_PAGE_SIZE,
        });
        setBeats(beatsData);
      } else {
        const versesData = await contentService.getVerses({
          ...verseFilters,
          offset: (page - 1) * HOME_PAGE_SIZE,
          limit: HOME_PAGE_SIZE,
        });
        setVerses(versesData);
      }
    } catch (err) {
      if (err instanceof Error) {
        console.error('Error fetching content:', err);
        setError('Error loading content. Please try again.');
      } else {
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, page]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Tab badges stay in the header even when that tab is closed, so refresh
  // both counts whenever filters change — not when the user merely switches tab.
  useEffect(() => {
    let cancelled = false;
    const { state: _rapperState, ...verseFilters } = filters;
    (async () => {
      try {
        const [beatsCountRes, versesCountRes] = await Promise.all([
          contentService.getBeatsCount(filters),
          contentService.getVersesCount(verseFilters),
        ]);
        if (cancelled) return;
        setTotalBeats(beatsCountRes.count || 0);
        setTotalVerses(versesCountRes.count || 0);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching feed counts:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, filters]);

  const total = activeTab === 'beats' ? totalBeats : totalVerses;
  const items = activeTab === 'beats' ? beats : verses;

  return {
    activeTab,
    setActiveTab,
    beats,
    verses,
    loading,
    error,
    filters,
    setFilters,
    page,
    setPage,
    totalBeats,
    totalVerses,
    total,
    items,
    fetchContent,
    genres: HOME_GENRES,
    states: HOME_STATES,
    pageSize: HOME_PAGE_SIZE,
  };
}
